import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { SmartbouffeLogoLarge } from '../components/Logo';
import { KeyIcon, EyeIcon, EyeOffIcon } from '../components/Icons';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1=enter email+code, 2=enter password
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Lire l'email depuis les query params
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [searchParams]);

  // Vérifier le code
  const verifyCode = async (e) => {
    e.preventDefault();
    
    if (!email || !code) {
      toast.error("Veuillez entrer votre email et le code");
      return;
    }

    if (code.length !== 6) {
      toast.error("Le code doit contenir 6 chiffres");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-reset-code`, {
        email: email,
        code: code
      });
      setStep(2);
      toast.success("Code vérifié !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code invalide ou expiré");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password-with-code`, {
        email: email,
        code: code,
        new_password: newPassword
      });
      toast.success("Mot de passe changé avec succès !");
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du changement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F5F1E8 0%, #E8E4D9 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '2rem',
        maxWidth: '400px',
        width: '100%',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <SmartbouffeLogoLarge style={{ width: '150px', margin: '0 auto 1rem' }} />
          <h1 style={{ fontSize: '1.5rem', color: '#3A4A3E', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
            <KeyIcon size={24} />
            Réinitialiser le mot de passe
          </h1>
        </div>

        {step === 1 && (
          <form onSubmit={verifyCode}>
            <p style={{ color: '#5D6F61', marginBottom: '1.5rem', textAlign: 'center' }}>
              Entrez votre email et le code à 6 chiffres reçu par email
            </p>
            
            <div style={{ marginBottom: '1rem' }}>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                data-testid="reset-email-input"
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <Label htmlFor="code">Code à 6 chiffres</Label>
              <Input
                id="code"
                type="text"
                value={code}
                onChange={(e) => {
                  // Accepter seulement les chiffres
                  const val = e.target.value.replace(/\D/g, '');
                  if (val.length <= 6) setCode(val);
                }}
                placeholder="123456"
                maxLength={6}
                style={{ fontSize: '1.5rem', textAlign: 'center', letterSpacing: '0.5rem' }}
                data-testid="reset-code-input"
              />
            </div>

            <Button 
              type="submit"
              disabled={loading || code.length < 6 || !email}
              style={{ width: '100%', background: '#87A96B' }}
              data-testid="verify-code-btn"
            >
              {loading ? 'Vérification...' : 'Vérifier le code'}
            </Button>

            <p style={{ color: '#8B7355', fontSize: '0.85rem', marginTop: '1rem', textAlign: 'center' }}>
              Pas reçu le code ? Vérifiez vos spams ou{' '}
              <button
                type="button"
                onClick={() => navigate('/')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#87A96B',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                demandez un nouveau code
              </button>
            </p>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleResetPassword}>
            <p style={{ color: '#5D6F61', marginBottom: '1.5rem', textAlign: 'center' }}>
              Créez un nouveau mot de passe pour<br />
              <strong>{email}</strong>
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <Label htmlFor="newPassword">Nouveau mot de passe</Label>
              <div style={{ position: 'relative' }}>
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  minLength={6}
                  style={{ paddingRight: '40px' }}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {showPassword ? (
                    <EyeOffIcon size={20} color="#8B7355" />
                  ) : (
                    <EyeIcon size={20} color="#8B7355" />
                  )}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <div style={{ position: 'relative' }}>
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  minLength={6}
                  style={{ paddingRight: '40px' }}
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {showConfirmPassword ? (
                    <EyeOffIcon size={20} color="#8B7355" />
                  ) : (
                    <EyeIcon size={20} color="#8B7355" />
                  )}
                </button>
              </div>
            </div>

            <Button 
              type="submit"
              disabled={loading}
              style={{ width: '100%', background: '#87A96B' }}
              data-testid="reset-password-btn"
            >
              {loading ? 'Changement...' : 'Changer le mot de passe'}
            </Button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none',
              border: 'none',
              color: '#87A96B',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            data-testid="back-to-login-btn"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    </div>
  );
}
